/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, September 13th 2022, 10:24:23 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useUserContext } from "authentication/providers/UserContextProvider";
import { useAPILoginForm } from "authentication/services/AuthAPI";
import { UserLoginCredentials } from "lib/schemas";
import sessionExpiredIcon from "theme/assets/images/icons/icons8-expired-96.png";
import {
  OSAPIForm,
  OSLoadingButton,
  VerticalStackWidget,
  FormControl,
  TextFormInput,
  PasswordFormInput,
  useToggleState,
} from "ui-widgets";
import { APIStatusNotification } from "ui-widgets/alerts/APIStatusNotification";
import { GoogleSignInButton } from "./GoogleSignInButton";

interface SessionExpiredNotificationProps {
  onRelogin?: () => void;
}
export const SessionExpiredNotification = (props: SessionExpiredNotificationProps) => {
  const { open, useAPIFormReturn, loggedInUser, isLoggingInUser, isFetchingUserDetails, handleLoginAnotherUser } =
    useSessionExpiredNotification(props);

  return (
    <APIStatusNotification
      open={open}
      title=""
      icon={<img src={sessionExpiredIcon} alt="" />}
      message="Oops! Your Session has expired due to inactivity. Don't worry though, just log back in"
      actions={null}
    >
      <p>
        {loggedInUser?.has_federated_account ? (
          <GoogleSignInButton />
        ) : (
          <OSAPIForm alertProps={{ horizontal: "center" }} useAPIFormReturn={useAPIFormReturn} showSubmitButton={false}>
            <div className="main-form-fields">
              <TextFormInput<UserLoginCredentials>
                schema_field_key="username"
                label="Username or Email Address"
                fullWidth
                autoTrim
                value={loggedInUser?.email}
                disabled={Boolean(loggedInUser?.email)}
              />

              <PasswordFormInput<UserLoginCredentials> schema_field_key="password" label="Confirm Password" fullWidth />

              <FormControl>
                <VerticalStackWidget>
                  <OSLoadingButton
                    className="button-style-A"
                    variant="contained"
                    size="large"
                    type="submit"
                    color="primary"
                    isLoading={isLoggingInUser || isFetchingUserDetails}
                  >
                    Continue
                  </OSLoadingButton>
                </VerticalStackWidget>
              </FormControl>
            </div>
          </OSAPIForm>
        )}
        <OSLoadingButton
          className="button-style-A"
          variant="text"
          size="large"
          type="button"
          color="primary"
          isLoading={false}
          onClick={handleLoginAnotherUser}
        >
          Login as another user
        </OSLoadingButton>
      </p>
    </APIStatusNotification>
  );
};

const useSessionExpiredNotification = ({ onRelogin }: SessionExpiredNotificationProps) => {
  const { loggedInUser, logoutUser } = useUserContext();

  const { useAPIFormReturn, isLoggingInUser, isFetchingUserDetails } = useAPILoginForm({
    nextURL: window.location.pathname,
  });

  const {
    useFormReturn: { setValue },
    isSuccessfulAPIDataSend,
  } = useAPIFormReturn;

  React.useEffect(() => {
    if (loggedInUser?.email) setValue("username", loggedInUser.email);
  }, [loggedInUser?.email, setValue]);

  const { handleClose, open } = useToggleState(true);

  const handleLoginAnotherUser = () => {
    logoutUser();
    handleClose();
  };

  React.useEffect(() => {
    if (isSuccessfulAPIDataSend) {
      handleClose();
      onRelogin?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessfulAPIDataSend, handleClose]);

  return { open, useAPIFormReturn, loggedInUser, isLoggingInUser, isFetchingUserDetails, handleLoginAnotherUser };
};
